import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from "./context/AuthContext";
import { BrandingProvider } from "./context/BrandingContext";
import { PlatformSettingsProvider } from "./context/PlatformSettingsContext";
import ProtectedRoute from "./components/ProtectedRoute";
import SuperAdminRoute from "./components/SuperAdminRoute";
import LoginPage from "./pages/LoginPage";
import CompanyLoginPage from "./pages/CompanyLoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import TechnicianDashboard from "./pages/TechnicianDashboard";
import TaskListPage from "./pages/TaskListPage";
import TaskDetailPage from "./pages/TaskDetailPage";
import CreateTaskPage from "./pages/CreateTaskPage";
import CalendarPage from "./pages/CalendarPage";
import ReportsPage from "./pages/ReportsPage";
import ProfilePage from "./pages/ProfilePage";
import UsersPage from "./pages/admin/UsersPage";
import LocationsPage from "./pages/admin/LocationsPage";
import CategoriesPage from "./pages/admin/CategoriesPage";
import InventoryPage from "./pages/admin/InventoryPage";
import SettingsPage from "./pages/admin/SettingsPage";
import SuperAdminPage from "./pages/admin/SuperAdminPage";
import PurchaseOrdersPage from "./pages/admin/PurchaseOrdersPage";
import PMSchedulesPage from "./pages/admin/PMSchedulesPage";
import WebhooksPage from "./pages/admin/WebhooksPage";
import TimeLogPage from "./pages/TimeLogPage";
import TimeReportPage from "./pages/TimeReportPage";
import BillingPage from "./pages/BillingPage";
// Super Admin Pages
import RevenuePage from "./pages/superadmin/RevenuePage";
import SuperAdminUsersPage from "./pages/superadmin/UsersPage";
import SubscriptionsPage from "./pages/superadmin/SubscriptionsPage";
import AnalyticsPage from "./pages/superadmin/AnalyticsPage";
import BackupRestorePage from "./pages/superadmin/BackupRestorePage";
import LandingPage from "./pages/LandingPage";
import LandingPageEditor from "./pages/admin/LandingPageEditor";
import LandingPagePreview from "./pages/LandingPagePreview";
import PricingPage from "./pages/PricingPage";
import LegalDraftPage from "./pages/LegalDraftPage";
import CompanySignupPage from "./pages/CompanySignupPage";
import SuperAdminSettingsPage from "./pages/superadmin/SuperAdminSettingsPage";
import ChatWidget from "./components/ChatWidget";

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <PlatformSettingsProvider>
          <BrandingProvider>
            <AuthProvider>
              <BrowserRouter>
                <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/landing" element={<Navigate to="/" replace />} />
                <Route path="/landing-preview" element={<LandingPagePreview />} />
                <Route path="/landing-optimized" element={<Navigate to="/" replace />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/privacy" element={<LegalDraftPage />} />
                <Route path="/terms" element={<LegalDraftPage />} />
                <Route path="/cookies" element={<LegalDraftPage />} />
                <Route path="/get-started" element={<CompanySignupPage />} />
                <Route path="/company-signup" element={<CompanySignupPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/login/:slug" element={<CompanyLoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
              
              {/* Protected Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/tasks" element={
                <ProtectedRoute>
                  <TaskListPage />
                </ProtectedRoute>
              } />
              <Route path="/tasks/new" element={
                <ProtectedRoute>
                  <CreateTaskPage />
                </ProtectedRoute>
              } />
              <Route path="/tasks/:id" element={
                <ProtectedRoute>
                  <TaskDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/my-work" element={
                <ProtectedRoute>
                  <TechnicianDashboard />
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute>
                  <ReportsPage />
                </ProtectedRoute>
              } />
              <Route path="/calendar" element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="/time-log" element={
                <ProtectedRoute>
                  <TimeLogPage />
                </ProtectedRoute>
              } />
              <Route path="/time-reports" element={
                <ProtectedRoute>
                  <TimeReportPage />
                </ProtectedRoute>
              } />
              <Route path="/billing" element={
                <ProtectedRoute>
                  <BillingPage />
                </ProtectedRoute>
              } />
              
              {/* Admin Routes */}
              <Route path="/admin/users" element={
                <ProtectedRoute>
                  <UsersPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/locations" element={
                <ProtectedRoute>
                  <LocationsPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/categories" element={
                <ProtectedRoute>
                  <CategoriesPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/inventory" element={
                <ProtectedRoute>
                  <InventoryPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/landing-editor" element={
                <ProtectedRoute>
                  <LandingPageEditor />
                </ProtectedRoute>
              } />
              <Route path="/admin/purchase-orders" element={
                <ProtectedRoute>
                  <PurchaseOrdersPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/pm-schedules" element={
                <ProtectedRoute>
                  <PMSchedulesPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/webhooks" element={
                <ProtectedRoute>
                  <WebhooksPage />
                </ProtectedRoute>
              } />
              <Route path="/super-admin" element={
                <SuperAdminRoute>
                  <SuperAdminPage />
                </SuperAdminRoute>
              } />
              
              {/* Super Admin Data Pages */}
              <Route path="/super-admin/revenue" element={
                <SuperAdminRoute>
                  <RevenuePage />
                </SuperAdminRoute>
              } />
              <Route path="/super-admin/users" element={
                <SuperAdminRoute>
                  <SuperAdminUsersPage />
                </SuperAdminRoute>
              } />
              <Route path="/super-admin/subscriptions" element={
                <SuperAdminRoute>
                  <SubscriptionsPage />
                </SuperAdminRoute>
              } />
              <Route path="/super-admin/analytics" element={
                <SuperAdminRoute>
                  <AnalyticsPage />
                </SuperAdminRoute>
              } />
              <Route path="/super-admin/settings" element={
                <SuperAdminRoute>
                  <SuperAdminSettingsPage />
                </SuperAdminRoute>
              } />
              <Route path="/super-admin/backup" element={
                <SuperAdminRoute>
                  <BackupRestorePage />
                </SuperAdminRoute>
              } />
              
              {/* Redirect root to dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            
            {/* AI Chat Widget - Available on authenticated pages only */}
            {window.location.pathname !== '/landing-preview' && window.location.pathname !== '/landing' && <ChatWidget />}
            </BrowserRouter>
          </AuthProvider>
        </BrandingProvider>
      </PlatformSettingsProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
